import React from 'react'
import { Box, ButtonBase, TableCell, TableSortLabel, Typography } from '@mui/material'
import { DataTableText } from '../../../shared/components/DataTable'
import { OverflowTooltipWrapper } from '../../../shared/components/OverflowTooltipWrapper/OverflowTooltipWrapper'
import { SortDirection } from '../CommentsView.helpers'

export const COMMENT_CELL_MAX_HEIGHT = 220

export const HeaderCell = ({
    children,
    width,
    isActive,
    direction,
    onClick,
}: {
    children: React.ReactNode
    width: number
    isActive: boolean
    direction: SortDirection
    onClick: () => void
}) => (
    <TableCell
        sx={{
            padding: '0 16px 12px',
            width,
            borderBottom: '2px solid',
            borderColor: 'figma.grayscale.stroke',
            overflow: 'hidden',
        }}
    >
        <TableSortLabel
            active={isActive}
            direction={direction}
            onClick={onClick}
            hideSortIcon={false}
            sx={{
                color: 'figma.grayscale.textHier2',
                maxWidth: '100%',
                '& .MuiTableSortLabel-icon': {
                    color: 'inherit !important',
                    flexShrink: 0,
                },
                '&:hover': {
                    color: 'figma.primary',
                },
                '&.Mui-active': {
                    color: 'figma.primary',
                },
            }}
        >
            <OverflowTooltipWrapper title={children} placement="top-start">
                <Typography variant="18x600" color="inherit" sx={{ minWidth: 0 }}>
                    {children}
                </Typography>
            </OverflowTooltipWrapper>
        </TableSortLabel>
    </TableCell>
)

export const BodyCell = ({
    children,
    width,
    align = 'left',
    verticalAlign = 'middle',
    isFirstColumn = false,
    isLastColumn = false,
    isFirstRow = false,
    isLastRow = false,
}: {
    children: React.ReactNode
    width: number
    align?: 'left' | 'center' | 'right'
    verticalAlign?: 'top' | 'middle'
    isFirstColumn?: boolean
    isLastColumn?: boolean
    isFirstRow?: boolean
    isLastRow?: boolean
}) => (
    <TableCell
        align={align}
        sx={{
            width,
            py: 2,
            px: 2,
            verticalAlign,
            ...(isFirstColumn && {
                borderTopLeftRadius: isFirstRow ? 2 : 0,
                borderBottomLeftRadius: isLastRow ? 2 : 0,
            }),
            ...(isLastColumn && {
                borderTopRightRadius: isFirstRow ? 2 : 0,
                borderBottomRightRadius: isLastRow ? 2 : 0,
            }),
        }}
    >
        {children}
    </TableCell>
)

export const ClickableOverflowText = ({ text, onClick }: { text: string; onClick: () => void }) => (
    <ButtonBase
        disableRipple
        onClick={onClick}
        sx={{
            alignItems: 'flex-start',
            color: 'figma.primary',
            display: 'block',
            textAlign: 'left',
            width: '100%',
        }}
    >
        <DataTableText text={text} color="inherit" variant="14x600" />
    </ButtonBase>
)

export const ScrollableFormattedText = ({
    text,
    color = 'figma.grayscale.textHier2',
    maxHeight = COMMENT_CELL_MAX_HEIGHT,
}: {
    text: string
    color?: string
    maxHeight?: number
}) => (
    <Box
        sx={{
            color,
            maxHeight,
            overflow: 'auto',
            overflowWrap: 'anywhere',
            pr: 1,
            whiteSpace: 'pre-wrap',
            width: '100%',
        }}
    >
        <Typography
            variant="14x400"
            color="inherit"
            component="div"
            sx={{
                whiteSpace: 'inherit',
            }}
        >
            {text}
        </Typography>
    </Box>
)
